import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BookOpen,
  Upload,
  Trash2,
  RefreshCw,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import apiService from '../services/apiService';

const STATUS_UI = {
  pending: { label: 'Bekliyor', className: 'bg-slate-500/15 text-slate-300' },
  uploading: { label: 'Yükleniyor', className: 'bg-sky-500/15 text-sky-300' },
  processing: { label: 'İşleniyor', className: 'bg-amber-500/15 text-amber-300' },
  ready: { label: 'Hazır', className: 'bg-emerald-500/15 text-emerald-300' },
  failed: { label: 'Hata', className: 'bg-rose-500/15 text-rose-300' },
};

function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CampaignKnowledgeBase({ isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [pineconeConfigured, setPineconeConfigured] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.fetchCampaignDocuments();
      setDocs(result.documents || []);
      setPineconeConfigured(Boolean(result.pineconeConfigured));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh processing docs every 8s
  useEffect(() => {
    const processing = docs.filter((d) => d.status === 'processing' || d.status === 'uploading');
    if (!processing.length) return undefined;
    const timer = setInterval(async () => {
      for (const doc of processing) {
        try {
          await apiService.refreshCampaignDocument(doc.id);
        } catch {
          /* ignore single refresh errors */
        }
      }
      await load();
    }, 8000);
    return () => clearInterval(timer);
  }, [docs, load]);

  const handleFiles = async (fileList) => {
    if (!isAdmin || !fileList?.length) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(fileList)) {
        await apiService.uploadCampaignDocument(file);
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRefresh = async (id) => {
    try {
      await apiService.refreshCampaignDocument(id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin || !window.confirm('Doküman silinsin mi? (Bilgi bankasından da kaldırılır)')) {
      return;
    }
    try {
      await apiService.deleteCampaignDocument(id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <PageHeader
        title="Kampanya Bilgi Bankası"
        description={
          <>
            <BookOpen className="h-4 w-4 text-teal-400" />
            <span>
              PDF / DOCX / TXT yükleyin — Facebook Messenger asistanı bu dosyalardan yanıt verir.
            </span>
          </>
        }
        badge={
          <span
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ring-1 ${
              pineconeConfigured
                ? 'bg-emerald-400/10 text-emerald-300 ring-emerald-400/20'
                : 'bg-amber-400/10 text-amber-300 ring-amber-400/20'
            }`}
          >
            {pineconeConfigured ? 'Bilgi bankası bağlı' : 'Bilgi bankası yapılandırılmamış'}
          </span>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {isAdmin && (
        <div
          className={`card mb-6 border-2 border-dashed transition-colors ${
            dragOver ? 'border-teal-400/60 bg-teal-500/5' : 'border-white/10'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
        >
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-600/20">
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-teal-300" />
              ) : (
                <Upload className="h-6 w-6 text-teal-300" />
              )}
            </div>
            <div>
              <p className="font-medium text-white">
                {uploading ? 'Yükleniyor…' : 'Kampanya dokümanını sürükleyin veya seçin'}
              </p>
              <p className="mt-1 text-sm text-slate-400">PDF, DOCX, TXT, MD — en fazla 20 MB</p>
            </div>
            <button
              type="button"
              className="btn-primary"
              disabled={uploading || !pineconeConfigured}
              onClick={() => inputRef.current?.click()}
            >
              Dosya Seç
            </button>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              accept=".pdf,.docx,.txt,.md,application/pdf,text/plain"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-slate-400">Yükleniyor…</p>
      ) : docs.length === 0 ? (
        <EmptyState
          title="Henüz doküman yok"
          description="Bir kampanya broşürü yükleyin; Messenger’daki AI asistanı bu içerikten yanıt verir."
        />
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-slate-500">
                <th className="px-4 py-3">Dosya</th>
                <th className="px-4 py-3">Boyut</th>
                <th className="px-4 py-3">Durum</th>
                <th className="px-4 py-3">Dosya ID</th>
                <th className="px-4 py-3">Tarih</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => {
                const st = STATUS_UI[doc.status] || STATUS_UI.pending;
                return (
                  <tr key={doc.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-white">
                        <FileText className="h-4 w-4 text-teal-400" />
                        <span className="font-medium">{doc.originalName}</span>
                      </div>
                      {doc.errorMessage && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-rose-400">
                          <AlertCircle className="h-3 w-3" /> {doc.errorMessage}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{formatBytes(doc.sizeBytes)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${st.className}`}>
                        {doc.status === 'ready' && <CheckCircle2 className="h-3 w-3" />}
                        {(doc.status === 'processing' || doc.status === 'uploading') && (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        )}
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">
                      {doc.pineconeFileId ? `${String(doc.pineconeFileId).slice(0, 12)}…` : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {doc.createdAt ? new Date(doc.createdAt).toLocaleString('tr-TR') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {(doc.status === 'processing' || doc.status === 'uploading') && (
                          <button
                            type="button"
                            className="btn-secondary p-2"
                            title="Durumu yenile"
                            onClick={() => handleRefresh(doc.id)}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            type="button"
                            className="btn-secondary p-2 text-rose-400"
                            title="Sil"
                            onClick={() => handleDelete(doc.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
